import { CollectionReference } from "@firebase/firestore-types";
import * as moment from "moment";
import { IServiceCharge } from "src/components/projects/project/statements/advanced/interfaces";
import {
  findAllCriterionMatches,
  findFirstCriterionMatch,
  findServiceCharges,
  IPdfPage,
  TIMEOUT,
} from "src/global";

export function importAPSStatementData(
  priorId: string | undefined,
  pdfPages: IPdfPage[],
  lastOnPeakGridReadDate: number,
  lastOnPeakGridReading: number,
  lastOnPeakSolarReadDate: number,
  lastOnPeakSolarReading: number,
  lastGridReadDate: number,
  lastGridReading: number,
  lastSolarReadDate: number,
  lastSolarReading: number,
  serviceChargesCollection: CollectionReference,
  taxesAndFeesCollection: CollectionReference
) {
  const statementDateFormat: string = "MMMM DD, YYYY";
  const readDateFormat: string = "MMM DD, YYYY";
  const statementDate = importStatementDate(pdfPages, statementDateFormat);
  const {
    gridReadDate,
    solarReadDate,
    solarGeneratedReadDate,
    priorGridReadDate,
    priorSolarReadDate,
  } = importMeterReadDates(
    priorId,
    pdfPages,
    statementDate,
    readDateFormat,
    lastGridReadDate,
    lastSolarReadDate
  );
  const { gridReading, solarReading, priorGridReading, priorSolarReading } = importMeterReadings(
    priorId,
    pdfPages,
    lastGridReading,
    lastSolarReading
  );
  // const {
  //   onPeakGridReadDate,
  //   onPeakSolarReadDate,
  //   priorOnPeakGridReadDate,
  //   priorOnPeakSolarReadDate,
  // } = importOnPeakReadDates(
  //   priorId,
  //   pdfPages,
  //   statementDate,
  //   readDateFormat,
  //   lastOnPeakGridReadDate,
  //   lastOnPeakSolarReadDate
  // );
  const onPeakGridReadDate = gridReadDate;
  const onPeakSolarReadDate = gridReadDate;
  let priorOnPeakGridReadDate = lastOnPeakGridReadDate;
  let priorOnPeakSolarReadDate = lastOnPeakSolarReadDate;
  if (!priorId) {
    priorOnPeakGridReadDate = priorGridReadDate;
    priorOnPeakSolarReadDate = priorGridReadDate;
  }
  const {
    onPeakGridReading,
    onPeakSolarReading,
    priorOnPeakGridReading,
    priorOnPeakSolarReading,
  } = importOnPeakReadings(priorId, pdfPages, lastOnPeakGridReading, lastOnPeakSolarReading);
  const { onPeakCredits, offPeakCredits } = importLastMonthCredits(priorId, pdfPages);
  const totalServiceCharges = importServiceCharges(pdfPages, serviceChargesCollection);
  const totalTaxesAndFees = importTaxesAndFees(pdfPages, taxesAndFeesCollection);
  const netElectricityCredit = importNetElectricityCredit(pdfPages);

  return {
    statementDate,
    gridReadDate,
    solarReadDate,
    solarGeneratedReadDate,
    priorGridReadDate,
    priorSolarReadDate,
    gridReading,
    solarReading,
    priorGridReading,
    priorSolarReading,
    onPeakGridReadDate,
    onPeakSolarReadDate,
    priorOnPeakGridReadDate,
    priorOnPeakSolarReadDate,
    onPeakGridReading,
    onPeakSolarReading,
    priorOnPeakGridReading,
    priorOnPeakSolarReading,
    onPeakCredits,
    offPeakCredits,
    totalServiceCharges,
    totalTaxesAndFees,
    netElectricityCredit,
  };
}

function importNetElectricityCredit(pdfPages: IPdfPage[]): any {
  const searchResult = findFirstCriterionMatch(pdfPages, "From your renewable system", true, true);
  let netElectricityCredit = NaN;
  if (searchResult.found) {
    if (searchResult.result.startsWith("-")) {
      netElectricityCredit = Number(searchResult.result.substring(2));
      netElectricityCredit = netElectricityCredit * -1.0;
    } else if (searchResult.result.startsWith("$")) {
      netElectricityCredit = Number(searchResult.result.substring(1));
    }
  }
  return netElectricityCredit;
}
function importServiceCharges(
  pdfPages: IPdfPage[],
  serviceChargesCollection: CollectionReference
): number {
  const searchResults = findServiceCharges(
    pdfPages,
    "Cost of net electricity",
    "Cost of electricity you used"
  );
  const serviceCharges: IServiceCharge[] = [];
  for (let i = 0; i < searchResults.length; i += 2) {
    const description = searchResults[i];
    let cost = 0.0;
    if (searchResults[i + 1].startsWith("$")) {
      cost = Number(searchResults[i + 1].substring(1));
    } else if (searchResults[i + 1].startsWith("-")) {
      cost = Number(searchResults[i + 1].substring(2));
      cost = cost * -1.0;
    }
    const serviceCharge: IServiceCharge = {
      id: "",
      description,
      unit: "Cost",
      value: cost,
      rate: 1.0,
      totalCharge: cost,
      timestamp: Date.now(),
    };
    serviceCharges.push(serviceCharge);
  }
  let totalServiceCharges = 0.0;
  serviceCharges.forEach(serviceCharge => {
    setTimeout(() => {
      serviceCharge.timestamp = Date.now();
      serviceChargesCollection.add(serviceCharge);
    }, TIMEOUT);
    totalServiceCharges += serviceCharge.totalCharge;
  });

  return totalServiceCharges;
}

function importTaxesAndFees(
  pdfPages: IPdfPage[],
  taxesAndFeesCollection: CollectionReference
): number {
  const searchResults = findServiceCharges(
    pdfPages,
    "Taxes and fees",
    "Cost of electricity with taxes and fees"
  );
  const taxesAndFees: IServiceCharge[] = [];
  for (let i = 0; i < searchResults.length; i += 2) {
    const description = searchResults[i];
    const cost = Number(searchResults[i + 1].substring(1));
    const taxAndFee: IServiceCharge = {
      id: "",
      description,
      unit: "Cost",
      value: cost,
      rate: 1.0,
      totalCharge: cost,
      timestamp: Date.now(),
    };
    taxesAndFees.push(taxAndFee);
  }
  let totalTaxesAndFees = 0.0;
  taxesAndFees.forEach(taxAndFee => {
    setTimeout(() => {
      taxAndFee.timestamp = Date.now();
      taxesAndFeesCollection.add(taxAndFee);
    }, TIMEOUT);
    totalTaxesAndFees += taxAndFee.totalCharge;
  });

  return totalTaxesAndFees;
}

function importLastMonthCredits(priorId: string | undefined, pdfPages: IPdfPage[]) {
  let onPeakCredits = 0;
  let offPeakCredits = 0;
  if (priorId === undefined) {
    let searchResult = findFirstCriterionMatch(
      pdfPages,
      "Last month's on-peak kWh credit",
      true,
      true
    );
    if (searchResult.found) {
      onPeakCredits = Number(searchResult.result);
    }
    searchResult = findFirstCriterionMatch(
      pdfPages,
      "Last month's off-peak kWh credit",
      true,
      true
    );
    if (searchResult.found) {
      offPeakCredits = Number(searchResult.result);
    }
  }
  return { onPeakCredits, offPeakCredits };
}

function importOnPeakReadings(
  priorId: string | undefined,
  pdfPages: IPdfPage[],
  lastOnPeakGridReading: number,
  lastOnPeakSolarReading: number
) {
  let onPeakGridReading = 0;
  let onPeakSolarReading = 0;
  let priorOnPeakGridReading = lastOnPeakGridReading;
  let priorOnPeakSolarReading = lastOnPeakSolarReading;
  const searchResults = findAllCriterionMatches(pdfPages, "On-peak meter reading on", true, false);
  if (searchResults.length === 4) {
    let pageIndex = searchResults[0].pdfPageNum;
    let wordIndex = searchResults[0].wordIndex;
    onPeakGridReading = Number(searchResults[0].result);
    if (isNaN(onPeakGridReading)) {
      onPeakGridReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
    }
    pageIndex = searchResults[2].pdfPageNum;
    wordIndex = searchResults[2].wordIndex;
    onPeakSolarReading = Number(searchResults[2].result);
    if (isNaN(onPeakSolarReading)) {
      onPeakSolarReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
    }
    if (priorId === undefined) {
      pageIndex = searchResults[1].pdfPageNum;
      wordIndex = searchResults[1].wordIndex;
      priorOnPeakGridReading = Number(searchResults[1].result);
      if (isNaN(priorOnPeakGridReading)) {
        priorOnPeakGridReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
      }
      pageIndex = searchResults[3].pdfPageNum;
      wordIndex = searchResults[3].wordIndex;
      priorOnPeakSolarReading = Number(searchResults[3].result);
      if (isNaN(priorOnPeakSolarReading)) {
        priorOnPeakSolarReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
      }
    }
  }
  if (isNaN(onPeakGridReading)) {
    onPeakGridReading = 0;
  }
  if (isNaN(priorOnPeakGridReading)) {
    priorOnPeakGridReading = 0;
  }
  if (isNaN(onPeakSolarReading)) {
    onPeakSolarReading = 0;
  }
  if (isNaN(priorOnPeakSolarReading)) {
    priorOnPeakSolarReading = 0;
  }
  return {
    onPeakGridReading,
    onPeakSolarReading,
    priorOnPeakGridReading,
    priorOnPeakSolarReading,
  };
}

export function importOnPeakReadDates(
  priorId: string | undefined,
  pdfPages: IPdfPage[],
  statementDate: number,
  readDateFormat: string,
  lastOnPeakGridReadDate: number,
  lastOnPeakSolarReadDate: number
) {
  let onPeakGridReadDate = Date.now();
  let onPeakSolarReadDate = Date.now();
  let priorOnPeakGridReadDate = lastOnPeakGridReadDate;
  let priorOnPeakSolarReadDate = lastOnPeakSolarReadDate;
  const statementDateYear = moment(statementDate).year();
  const searchResults = findAllCriterionMatches(pdfPages, "On-peak meter reading on", false, false);
  if (searchResults.length === 4) {
    let date: string = "";
    if (searchResults[0].result.length > 24) {
      date = searchResults[0].result.substring(25) + ", " + statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        onPeakGridReadDate = moment(date, readDateFormat).valueOf();
      }
    } else {
      // The read date is split between two words.
      const pageIndex = searchResults[0].pdfPageNum;
      const wordIndex = searchResults[0].wordIndex;
      date =
        pdfPages[pageIndex].words[wordIndex + 1] +
        " " +
        pdfPages[pageIndex].words[wordIndex + 2] +
        ", " +
        statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        onPeakGridReadDate = moment(date, readDateFormat).valueOf();
      }
    }
    if (searchResults[2].result.length > 24) {
      date = searchResults[2].result.substring(25) + ", " + statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        onPeakSolarReadDate = moment(date, readDateFormat).valueOf();
      }
    } else {
      const pageIndex = searchResults[2].pdfPageNum;
      const wordIndex = searchResults[2].wordIndex;
      date =
        pdfPages[pageIndex].words[wordIndex + 1] +
        " " +
        pdfPages[pageIndex].words[wordIndex + 2] +
        ", " +
        statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        onPeakSolarReadDate = moment(date, readDateFormat).valueOf();
      }
    }
    if (!priorId) {
      if (searchResults[1].result.length > 24) {
        date = searchResults[1].result.substring(24) + ", " + statementDateYear;
        if (moment(date, readDateFormat).isValid()) {
          priorOnPeakGridReadDate = moment(date, readDateFormat).valueOf();
        }
      } else {
        const pageIndex = searchResults[1].pdfPageNum;
        const wordIndex = searchResults[1].wordIndex;
        date =
          pdfPages[pageIndex].words[wordIndex + 1] +
          " " +
          pdfPages[pageIndex].words[wordIndex + 2] +
          ", " +
          statementDateYear;
        if (moment(date, readDateFormat).isValid()) {
          priorOnPeakGridReadDate = moment(date, readDateFormat).valueOf();
        }
      }
      if (searchResults[3].result.length > 24) {
        date = searchResults[3].result.substring(24) + ", " + statementDateYear;
        if (moment(date, readDateFormat).isValid()) {
          priorOnPeakSolarReadDate = moment(date, readDateFormat).valueOf();
        }
      } else {
        const pageIndex = searchResults[3].pdfPageNum;
        const wordIndex = searchResults[3].wordIndex;
        date =
          pdfPages[pageIndex].words[wordIndex + 1] +
          " " +
          pdfPages[pageIndex].words[wordIndex + 2] +
          ", " +
          statementDateYear;
        if (moment(date, readDateFormat).isValid()) {
          priorOnPeakSolarReadDate = moment(date, readDateFormat).valueOf();
        }
      }
    }
  }
  return {
    onPeakGridReadDate,
    onPeakSolarReadDate,
    priorOnPeakGridReadDate,
    priorOnPeakSolarReadDate,
  };
}

function importMeterReadings(
  priorId: string | undefined,
  pdfPages: IPdfPage[],
  lastGridReading: number,
  lastSolarReading: number
) {
  const searchResults = findAllCriterionMatches(pdfPages, "Meter reading on", true, false);
  let gridReading: number = 0;
  let solarReading: number = 0;
  let priorGridReading = lastGridReading;
  let priorSolarReading = lastSolarReading;
  if (searchResults.length === 4) {
    let pageIndex = searchResults[0].pdfPageNum;
    let wordIndex = searchResults[0].wordIndex;
    // These are the meter readings directly from the bill.  0=current grid; 1=prior grid; 2=current solar; 3=prior solar.
    gridReading = Number(searchResults[0].result);
    if (isNaN(gridReading)) {
      gridReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
    }
    pageIndex = searchResults[2].pdfPageNum;
    wordIndex = searchResults[2].wordIndex;
    solarReading = Number(searchResults[2].result);
    if (isNaN(solarReading)) {
      solarReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
    }
    if (priorId === undefined) {
      pageIndex = searchResults[1].pdfPageNum;
      wordIndex = searchResults[1].wordIndex;
      priorGridReading = Number(searchResults[1].result);
      if (isNaN(priorGridReading)) {
        priorGridReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
      }
      pageIndex = searchResults[3].pdfPageNum;
      wordIndex = searchResults[3].wordIndex;
      priorSolarReading = Number(searchResults[3].result);
      if (isNaN(priorSolarReading)) {
        priorSolarReading = Number(pdfPages[pageIndex].words[wordIndex + 2]);
      }
    }
  }
  if (isNaN(gridReading)) {
    gridReading = 0;
  }
  if (isNaN(priorGridReading)) {
    priorGridReading = 0;
  }
  if (isNaN(solarReading)) {
    solarReading = 0;
  }
  if (isNaN(priorSolarReading)) {
    priorSolarReading = 0;
  }
  return { gridReading, solarReading, priorGridReading, priorSolarReading };
}

function importMeterReadDates(
  priorId: string | undefined,
  pdfPages: IPdfPage[],
  statementDate: number,
  readDateFormat: string,
  lastGridReadDate: number,
  lastSolarReadDate: number
) {
  const searchResults = findAllCriterionMatches(pdfPages, "Meter reading on", false, false);
  let gridReadDate = Date.now();
  let solarReadDate = Date.now();
  let solarGeneratedReadDate = Date.now();
  let priorGridReadDate = lastGridReadDate;
  let priorSolarReadDate = lastSolarReadDate;
  const statementDateYear = moment(statementDate).year();
  if (searchResults.length === 4) {
    // Expected to be 4 results.  First 2 are Grid, second 2 are Solar received.
    let date: string = "";
    if (searchResults[0].result.length > 17) {
      date = searchResults[0].result.substring(17) + ", " + moment(statementDate).year();
      if (moment(date, readDateFormat).isValid()) {
        gridReadDate = moment(date, readDateFormat).valueOf();
      }
    } else {
      // The read date is split between two words.
      const pageIndex = searchResults[0].pdfPageNum;
      const wordIndex = searchResults[0].wordIndex;
      date =
        pdfPages[pageIndex].words[wordIndex + 1] +
        " " +
        pdfPages[pageIndex].words[wordIndex + 2] +
        " " +
        statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        gridReadDate = moment(date, readDateFormat).valueOf();
      }
    }
    if (searchResults[2].result.length > 17) {
      date = searchResults[2].result.substring(17) + ", " + moment(statementDate).year();
      if (moment(date, readDateFormat).isValid()) {
        solarReadDate = moment(date, readDateFormat).valueOf();
        solarGeneratedReadDate = solarReadDate;
      }
    } else {
      const pageIndex = searchResults[2].pdfPageNum;
      const wordIndex = searchResults[2].wordIndex;
      date =
        pdfPages[pageIndex].words[wordIndex + 1] +
        " " +
        pdfPages[pageIndex].words[wordIndex + 2] +
        " " +
        statementDateYear;
      if (moment(date, readDateFormat).isValid()) {
        solarReadDate = moment(date, readDateFormat).valueOf();
        solarGeneratedReadDate = solarReadDate;
      }
    }
    if (priorId === undefined) {
      const statementMonth = moment(statementDate).month();
      const monthAdjustment = statementMonth === 0 ? -1 : 0;
      if (searchResults[1].result.length > 17) {
        date =
          searchResults[1].result.substring(17) +
          ", " +
          (moment(statementDate).year() + monthAdjustment);
        if (moment(date, readDateFormat).isValid()) {
          priorGridReadDate = moment(date, readDateFormat).valueOf();
        }
      } else {
        const pageIndex = searchResults[1].pdfPageNum;
        const wordIndex = searchResults[1].wordIndex;
        date =
          pdfPages[pageIndex].words[wordIndex + 1] +
          " " +
          pdfPages[pageIndex].words[wordIndex + 2] +
          " " +
          (statementDateYear + monthAdjustment);
        if (moment(date, readDateFormat).isValid()) {
          priorGridReadDate = moment(date, readDateFormat).valueOf();
        }
      }
      if (searchResults[3].result.length > 17) {
        date = searchResults[3].result.substring(17) + ", " + moment(statementDate).year();
        if (moment(date, readDateFormat).isValid()) {
          priorSolarReadDate = moment(date, readDateFormat).valueOf();
        }
      } else {
        const pageIndex = searchResults[3].pdfPageNum;
        const wordIndex = searchResults[3].wordIndex;
        date =
          pdfPages[pageIndex].words[wordIndex + 1] +
          " " +
          pdfPages[pageIndex].words[wordIndex + 2] +
          " " +
          (statementDateYear + monthAdjustment);
        if (moment(date, readDateFormat).isValid()) {
          priorSolarReadDate = moment(date, readDateFormat).valueOf();
        }
      }
    }
  }

  return {
    gridReadDate,
    solarReadDate,
    solarGeneratedReadDate,
    priorGridReadDate,
    priorSolarReadDate,
  };
}

function importStatementDate(pdfPages: IPdfPage[], statementDateFormat: string): number {
  const searchResult = findFirstCriterionMatch(pdfPages, "Bill date:", false, false);
  let statementDate = Date.now();
  if (searchResult.found) {
    if (searchResult.result.length > 11) {
      const date = searchResult.result.substring(11);
      if (moment(date, statementDateFormat).isValid()) {
        statementDate = moment(date, statementDateFormat).valueOf();
      }
    } else {
      const month = pdfPages[searchResult.pdfPageNum].words[searchResult.wordIndex + 1];
      const dayYear = pdfPages[searchResult.pdfPageNum].words[searchResult.wordIndex + 2];
      const date = month + " " + dayYear;
      if (moment(date, statementDateFormat).isValid()) {
        statementDate = moment(date, statementDateFormat).valueOf();
      }
    }
  }
  return statementDate;
}



// WEBPACK FOOTER //
// ./src/pages/projects/project/statements/advanced/dualrate/APSStatementImporter.tsx